import {
  BrandCampaignPostItemDTO,
  BrandMgSupplierCampaignItem,
  BrandMgSupplierCampaignItemLookup,
  BrandMgSupplierCampaignItemLookupDTO,
  MgSupplierItemLookupDTO,
} from '@retail/campaign/types';
import { MgSupplierItemSearchContainer } from '@retail/products/containers';
import { useSelectedOrgUnit, useUserBrands } from '@retail/app/stores/selected-context';
import { AddItemsToCampaignDialogBase } from './AddItemsToCampaignDialogBase';
import {
  AddCampaignItemsSchema,
  BrandCampaignImportItem,
  BrandCampaignItemSchema,
  BrandCampaignItemsImportConfig,
  useGetAddBrandCampaignItemsColumns,
} from '@retail/campaign/components';
import { useFormContext } from 'react-hook-form';
import { useLookupCampaignItems } from '@retail/campaign/data-access';
import { useCallback, useState } from 'react';

interface Props {
  campaignId: string;
  open: boolean;
  onClose: () => void;
}

export const BrandCampaignDetailsAddItemsDialog = ({ campaignId, open, onClose }: Props) => {
  const { assortmentId, appTenant } = useSelectedOrgUnit();
  const { selectedBrand } = useUserBrands();

  const [lookupItemErrors, setLookupItemErrors] = useState<BrandMgSupplierCampaignItemLookupDTO[]>(
    []
  );

  const getCampaignItemColumns = useGetAddBrandCampaignItemsColumns({
    externalSourceType: appTenant.externalItemSource,
    showPackageDiscounts: selectedBrand.wholesaler,
  });

  const formMethods = useFormContext<AddCampaignItemsSchema<BrandCampaignItemSchema>>();

  const { mutateAsync: handleLookupItems, isPending: isPendingLookupItems } =
    useLookupCampaignItems<BrandMgSupplierCampaignItemLookupDTO>();

  const performItemsLookup = useCallback(
    async (dto: MgSupplierItemLookupDTO[]): Promise<BrandMgSupplierCampaignItem[]> => {
      const response = await handleLookupItems({ body: dto });
      const itemsWithErrors = response.filter((item) => item.errorCode);
      if (itemsWithErrors.length) {
        setLookupItemErrors(itemsWithErrors);
        return [];
      }
      return response
        .map((dto) => BrandMgSupplierCampaignItemLookup.fromDto(dto))
        .flatMap(({ items }) => items ?? []);
    },
    [handleLookupItems]
  );

  return (
    <AddItemsToCampaignDialogBase<
      BrandMgSupplierCampaignItem,
      BrandCampaignPostItemDTO,
      BrandMgSupplierCampaignItemLookupDTO,
      BrandCampaignItemSchema,
      BrandCampaignImportItem
    >
      open={open}
      onClose={onClose}
      campaignId={campaignId}
      campaignType="brand"
      itemsFormMethods={formMethods}
      importConfig={BrandCampaignItemsImportConfig.instance}
      renderItemSearch={({ selectedItems, setSelectedItems }) => (
        <MgSupplierItemSearchContainer
          assortmentId={assortmentId}
          checkedItems={selectedItems}
          setCheckedItems={setSelectedItems}
        />
      )}
      transformToPostDTO={(item, lookupItem) => ({
        mgSupplierId: lookupItem.supplier.id,
        mgItemNumber: lookupItem.mgItemNumber,
        campaignUnit: item.campaignUnit,
        includeVat: true,
        campaignSalesPrice: item.campaignSalesPrice ?? null,
        secondaryDiscountPercentage: selectedBrand.wholesaler
          ? item.secondaryDiscountPercentage ?? undefined
          : undefined,
        tertiaryDiscountPercentage: selectedBrand.wholesaler
          ? item.tertiaryDiscountPercentage ?? undefined
          : undefined,
        campaignPurchasePrice: item.campaignPurchasePrice ?? undefined,
        isSalesPriceOverridable: !item.fixedSalesPrice,
      })}
      getInitSchemaItem={(item) =>
        // @ts-ignore
        ({
          campaignUnit: item.getPrimaryPackageUnit()?.unit ?? item.packages[0].unit.unit ?? '',
          campaignSalesPrice: '',
          secondaryDiscountPercentage: '',
          tertiaryDiscountPercentage: '',
          campaignPurchasePrice: '',
          fixedSalesPrice: true,
        } as BrandCampaignItemSchema)
      }
      mapLookupItemToSchemaItem={(lookupItem, importItem) => {
        return {
          campaignUnit: importItem?.campaignUnit ?? lookupItem.getPrimaryPackageUnit()?.unit ?? '-',
          campaignSalesPrice: importItem?.salesPrice ?? '',
          secondaryDiscountPercentage: importItem?.secondaryDiscountPercentage ?? '',
          tertiaryDiscountPercentage: importItem?.tertiaryDiscountPercentage ?? '',
          campaignPurchasePrice: importItem?.purchasePrice ?? '',
          fixedSalesPrice: !!importItem?.imposedSalesPrice,
        } as BrandCampaignItemSchema;
      }}
      getCampaignItemsColumns={getCampaignItemColumns}
      isPendingLookupItems={isPendingLookupItems}
      performItemsLookup={performItemsLookup}
      lookupItemErrors={lookupItemErrors}
      setLookupItemErrors={setLookupItemErrors}
    />
  );
};
